






























import {
  computed,
  defineComponent,
  onMounted,
  PropType,
  ref,
  watch,
} from '@nuxtjs/composition-api';
import { Logger } from '@vue-storefront/core';

import Selection from '~/components/Selection/Selection.vue';
import useBikeVehicleFacets from '~/composables/useBikeVehicleFacets';
import {
  BIKE_SELECTOR_FIELDS,
  BikeSelectorField,
  IBikeSelection,
  IBikeSelectorOption,
  IBikeSelectorOptions,
  toMakeOptions,
  toModelOptions,
  toYearOptions,
} from '~/helpers/bikeSelection';

type FieldStatus = 'idle' | 'loading' | 'error';
type FieldRecord<T> = Record<BikeSelectorField, T>;

const FIELD_LABELS: FieldRecord<string> = { make: 'Make', model: 'Model', year: 'Year' };
const FIELD_PLURALS: FieldRecord<string> = { make: 'makes', model: 'models', year: 'years' };
const OPTIONS_KEY: FieldRecord<keyof IBikeSelectorOptions> = {
  make: 'makes',
  model: 'models',
  year: 'years',
};
const PARENT_FIELD: Partial<FieldRecord<BikeSelectorField>> = { model: 'make', year: 'model' };

/**
 * Cascading Make -> Model -> Year dropdowns. Purely a selection control: it
 * emits `change` on every pick and never navigates — the parent decides
 * whether a pick routes immediately (bike page) or waits for a submit
 * (hero, make popup).
 */
export default defineComponent({
  name: 'BikeSelector',
  components: {
    Selection,
  },
  props: {
    value: {
      type: Object as PropType<IBikeSelection>,
      default: () => ({}),
    },
    options: {
      type: Object as PropType<IBikeSelectorOptions>,
      default: () => ({}),
    },
    fields: {
      type: Array as PropType<BikeSelectorField[]>,
      default: () => [...BIKE_SELECTOR_FIELDS],
    },
    layout: {
      type: String as PropType<'inline' | 'stacked'>,
      default: 'stacked',
    },
    idPrefix: {
      type: String,
      default: 'bike-selector',
    },
  },
  // eslint-disable-next-line max-lines-per-function
  setup(props, { emit }) {
    const { fetchMakes, fetchSubmodels, getSubmodelYearRange } = useBikeVehicleFacets(
      props.idPrefix,
    );

    const selection = ref<IBikeSelection>({ ...props.value });
    const fieldOptions = ref<FieldRecord<IBikeSelectorOption[]>>({
      make: props.options.makes ?? [],
      model: props.options.models ?? [],
      year: props.options.years ?? [],
    });
    const status = ref<FieldRecord<FieldStatus>>({ make: 'idle', model: 'idle', year: 'idle' });

    // Bumped whenever a field's options are requested or cleared, so a slow
    // response for a stale parent selection can't overwrite newer options.
    const requestIds: FieldRecord<number> = { make: 0, model: 0, year: 0 };

    const setOptions = (field: BikeSelectorField, options: IBikeSelectorOption[]) => {
      fieldOptions.value = { ...fieldOptions.value, [field]: options };
    };

    const setStatus = (field: BikeSelectorField, value: FieldStatus) => {
      status.value = { ...status.value, [field]: value };
    };

    const load = async (field: BikeSelectorField, loader: () => Promise<IBikeSelectorOption[]>) => {
      const requestId = ++requestIds[field];
      setStatus(field, 'loading');
      try {
        const options = await loader();
        if (requestId !== requestIds[field]) return;
        setOptions(field, options);
        setStatus(field, 'idle');
      } catch (error) {
        if (requestId !== requestIds[field]) return;
        Logger.error(`BikeSelector: failed to load ${FIELD_PLURALS[field]}`, error);
        setOptions(field, []);
        setStatus(field, 'error');
      }
    };

    const loadMakes = () => load('make', async () => toMakeOptions(await fetchMakes()));

    const loadModels = (make: IBikeSelectorOption) =>
      load('model', async () => toModelOptions(await fetchSubmodels(make.title)));

    const loadYears = (make: IBikeSelectorOption, model: IBikeSelectorOption) =>
      load('year', async () => toYearOptions(await getSubmodelYearRange(make.title, model.title)));

    const clearField = (field: BikeSelectorField) => {
      requestIds[field]++;
      setOptions(field, []);
      setStatus(field, 'idle');
    };

    const hasParentSelection = (field: BikeSelectorField) => {
      const parent = PARENT_FIELD[field];
      return !parent || !!selection.value[parent];
    };

    const isDisabled = (field: BikeSelectorField) =>
      status.value[field] !== 'idle' ||
      !hasParentSelection(field) ||
      !fieldOptions.value[field].length;

    const getPlaceholder = (field: BikeSelectorField) => {
      if (status.value[field] === 'loading') return 'Loading…';
      if (status.value[field] === 'error') return `Couldn't load ${FIELD_PLURALS[field]}`;
      if (hasParentSelection(field) && !fieldOptions.value[field].length) {
        return `No ${FIELD_PLURALS[field]} found`;
      }
      return `Select ${field}`;
    };

    const getSelectionConfig = (field: BikeSelectorField) => ({
      title: FIELD_LABELS[field],
      label: field,
      queryField: field,
      isDisabled: isDisabled(field),
      options: fieldOptions.value[field].map(({ title, slug }) => ({ title, value: slug })),
    });

    const currentSelections = computed(() =>
      BIKE_SELECTOR_FIELDS.filter((field) => selection.value[field]).map((field) => ({
        title: FIELD_LABELS[field],
        value: (selection.value[field] as IBikeSelectorOption).title,
      })),
    );

    const nextField = computed(() =>
      props.fields.find((field) => !selection.value[field] && !isDisabled(field)),
    );

    // A field still loading its options is about to become usable, so it isn't dimmed.
    const isDimmed = (field: BikeSelectorField) =>
      isDisabled(field) && status.value[field] !== 'loading';

    const isDone = (field: BikeSelectorField) => !!selection.value[field];

    const getFieldClasses = (field: BikeSelectorField) => ({
      'bike-selector__field--next': field === nextField.value,
      'bike-selector__field--done': isDone(field),
      'bike-selector__field--disabled': isDimmed(field),
    });

    // `Selection` reports the picked option by its title (the clicked element's id).
    const onSelected = (field: BikeSelectorField, { value: optionTitle }: { value: string }) => {
      const option = fieldOptions.value[field].find(({ title }) => title === optionTitle);
      if (!option) return;

      const fieldIndex = BIKE_SELECTOR_FIELDS.indexOf(field);
      const next: IBikeSelection = {};
      BIKE_SELECTOR_FIELDS.slice(0, fieldIndex).forEach((parent) => {
        if (selection.value[parent]) next[parent] = selection.value[parent];
      });
      next[field] = option;

      selection.value = next;
      BIKE_SELECTOR_FIELDS.slice(fieldIndex + 1).forEach(clearField);
      emit('change', { selection: next, field });

      if (field === 'make') loadModels(option);
      if (field === 'model' && next.make) loadYears(next.make, option);
    };

    // Fetches whatever the parent didn't supply and we don't already hold.
    const loadMissing = () => {
      const { make, model } = selection.value;
      const needs = (field: BikeSelectorField) =>
        !props.options[OPTIONS_KEY[field]] &&
        !fieldOptions.value[field].length &&
        status.value[field] === 'idle';
      if (props.fields.includes('make') && needs('make')) loadMakes();
      if (make && needs('model')) loadModels(make);
      if (make && model && needs('year')) loadYears(make, model);
    };

    // Parents pass fresh object literals on every render, so resync is keyed on
    // content (slugs), never on object identity, or a re-render would wipe picks.
    const lineageKey = (source: IBikeSelection, field: BikeSelectorField) =>
      BIKE_SELECTOR_FIELDS.slice(0, BIKE_SELECTOR_FIELDS.indexOf(field))
        .map((parent) => source[parent]?.slug ?? '')
        .join('/');

    const valueKey = computed(() =>
      BIKE_SELECTOR_FIELDS.map((field) => props.value[field]?.slug ?? '').join('/'),
    );

    const optionsKeyFor = (field: BikeSelectorField) => {
      const provided = props.options[OPTIONS_KEY[field]];
      return provided ? `${provided.length}:${provided.map(({ slug }) => slug).join(',')}` : '-';
    };

    const optionsKey = computed(() => BIKE_SELECTOR_FIELDS.map(optionsKeyFor).join('|'));

    let syncedValueKey = valueKey.value;
    let syncedOptionKeys = BIKE_SELECTOR_FIELDS.map(optionsKeyFor);

    const resync = () => {
      const previous = selection.value;
      const incoming: IBikeSelection = { ...props.value };
      const nextOptionKeys = BIKE_SELECTOR_FIELDS.map(optionsKeyFor);

      // Only adopt the parent's value when its content changed; an options-only
      // change must not wipe a pick the parent never echoed back.
      const valueChanged = valueKey.value !== syncedValueKey;
      if (valueChanged) selection.value = incoming;
      syncedValueKey = valueKey.value;

      BIKE_SELECTOR_FIELDS.forEach((field, index) => {
        const provided = props.options[OPTIONS_KEY[field]];
        const parentChanged =
          valueChanged && lineageKey(previous, field) !== lineageKey(incoming, field);
        const optionsChanged = nextOptionKeys[index] !== syncedOptionKeys[index];

        if (provided && (optionsChanged || parentChanged)) {
          requestIds[field]++;
          setOptions(field, provided);
          setStatus(field, 'idle');
        } else if (parentChanged) {
          clearField(field);
        }
      });
      syncedOptionKeys = nextOptionKeys;

      loadMissing();
    };

    watch([valueKey, optionsKey], resync);

    onMounted(loadMissing);

    return {
      FIELD_LABELS,
      currentSelections,
      nextField,
      getFieldClasses,
      isDone,
      getSelectionConfig,
      getPlaceholder,
      onSelected,
    };
  },
});
